import { ApiKeyForm } from "@/components/orgs/api-key-form";
import { ApiKeysList } from "@/components/orgs/api-keys-list";
import { PendingApiKeysComponent } from "@/components/pending";
import { AuthUIContext } from "@papier/auth-ui";
import { type ApiKeyCreateUpdateFormSchema } from "@papier/shared/schemas/routers/api-keys.zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@papier/ui/components/ui/card";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { use } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/organization/api-keys")({
  component: RouteComponent,
});

function RouteComponent() {
  const { queryClient, trpc } = Route.useRouteContext();
  const {
    authClient,
    hooks: { useHasPermission },
  } = use(AuthUIContext);

  const { data: activeOrganization, isPending: isOrganizationPending } =
    authClient.useActiveOrganization();
  const { data: hasPermission, isPending: isPermissionPending } = useHasPermission({
    organizationId: activeOrganization?.id,
    permissions: {
      organization: ["update"],
    },
  });

  const {
    mutateAsync: createKeyMutate,
    isSuccess,
    error,
  } = useMutation(
    trpc.apiKeys.create.mutationOptions({
      onSuccess: () => {
        toast.success("API key created successfully");
        return queryClient.invalidateQueries(trpc.apiKeys.getAll.queryFilter());
      },
      onError: (error) => {
        toast.error("Failed to create API key", {
          description: error.message,
        });
      },
    }),
  );

  const onCreateSubmit = async (data: ApiKeyCreateUpdateFormSchema) => {
    await createKeyMutate({
      name: data.name,
      authorizationConfig: data.access === "WHOLE_ORG" ? "WHOLE_ORG" : data.projects,
    });
  };

  if (isOrganizationPending || isPermissionPending) {
    return <PendingApiKeysComponent />;
  }

  return (
    <main className="grid gap-4 md:gap-6">
      {hasPermission?.success && (
        <Card className="pb-0">
          <CardHeader>
            <CardTitle className="text-lg md:text-xl">Create API Key</CardTitle>
            <CardDescription>Create a new API key for your organization.</CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            <ApiKeyForm onSubmit={onCreateSubmit} isSuccess={isSuccess} error={error?.message} />
          </CardContent>
        </Card>
      )}

      <ApiKeysList hasPermission={hasPermission?.success} />
    </main>
  );
}
